import { MagnifyingGlass, type IconGlyph } from "@/icons";
import { cn } from "@/lib/cn";
import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type ReactNode } from "react";
import { Button } from "./Button";
import { Icon } from "./Icon";
import { IconBadge } from "./IconBadge";
import { Heading, Text } from "./Typography";

/** Classes of `EmptyState`: vertical rhythm per context (`screen` centres it in the viewport under the app header); wrap it in `PageContainer` for a page gutter. */
export const emptyStateVariants = cva("flex w-full flex-col items-center gap-5 text-center", {
  variants: {
    size: {
      page: "pb-12 pt-32",
      screen: "min-h-screen-body justify-center py-12",
      section: "py-12",
      inline: "py-6",
    },
  },
  defaultVariants: { size: "section" },
});

type EmptyStateSize = NonNullable<VariantProps<typeof emptyStateVariants>["size"]>;

const BADGE_SIZE = { page: "2xl", screen: "2xl", section: "xl", inline: "lg" } as const satisfies Record<EmptyStateSize, string>;

/** Props of `EmptyState`; pass an `icon` glyph (drawn in a neutral badge) or a custom `illustration`. */
export type EmptyStateProps = VariantProps<typeof emptyStateVariants> & {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  icon?: IconGlyph;
  illustration?: ReactNode;
  headingLevel?: 1 | 2 | 3;
  className?: string;
};

/** Centered "nothing here" block: visual, title, description and an optional action. */
export const EmptyState = forwardRef<HTMLDivElement, EmptyStateProps>(function EmptyState(
  { size, title, description, action, icon, illustration, headingLevel = 2, className },
  ref,
) {
  return (
    <div ref={ref} className={cn(emptyStateVariants({ size }), className)}>
      {illustration ??
        (icon && <IconBadge icon={icon} tone="neutral" variant="soft" size={BADGE_SIZE[size ?? "section"]} />)}
      <div className="flex flex-col gap-1">
        <Heading level={headingLevel} size="title">
          {title}
        </Heading>
        {description && <Text tone="muted">{description}</Text>}
      </div>
      {action && <div className="mt-4 w-full max-w-sm">{action}</div>}
    </div>
  );
});

/** Props of `SearchEmpty`; `title` and `description` override the generic copy built from `term`. */
export type SearchEmptyProps = {
  term?: string;
  onClear?: () => void;
  title?: ReactNode;
  description?: ReactNode;
  headingLevel?: 2 | 3;
  className?: string;
};

/** Empty search result preset with an optional "Limpar pesquisa" action. */
export function SearchEmpty({
  term,
  onClear,
  title,
  description = "Tente buscar por outro termo.",
  headingLevel = 2,
  className,
}: SearchEmptyProps) {
  return (
    <EmptyState
      size="section"
      headingLevel={headingLevel}
      className={className}
      illustration={<Icon as={MagnifyingGlass} size="4xl" tone="subtle" />}
      title={title ?? (term ? `Nenhum resultado para "${term}"` : "Nenhum resultado encontrado")}
      description={description}
      action={
        onClear && (
          <Button variant="link" tone="primary" size="auto" onClick={onClear}>
            Limpar pesquisa
          </Button>
        )
      }
    />
  );
}
